import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { Box, Stack } from "@mui/material";
import { Card } from "@src/appV2/redesign/system/ui-components";
import { isBefore } from "date-fns";

import { LoadingButton } from "../../components/LoadingButton";
import { PayInfoLabel } from "../../Pay/PayInfoLabel";
import { ShortLeadTimePill } from "../../Shift/ArrivalTime/Pill";
import { ShiftCardDivider } from "../../Shift/Card/CardDivider";
import { BookingCardContentWrapper } from "../../Shift/CardContentWrapper";
import { ShiftCardSummaryInfo } from "../../Shift/Open/CardSummaryInfo";
import { type TimeSlot } from "../../Shift/Open/types";
import { ShiftCardPillsWrapper } from "../../Shift/PillsWrapper";
import { ShiftQualificationLabel } from "../../Shift/QualificationLabel";
import { BookingCardSummaryWrapper } from "../../Shift/SummaryWrapper";
import { TimeSlotPill } from "../../Shift/TimeSlotPill";
import { ShiftInviteStatusPill } from "./ShiftInviteStatusPill";

interface ShiftInviteCardProps {
  timeSlot: TimeSlot;
  shiftStart: Date;
  shiftEnd: Date;
  expiresAt: Date;
  shiftTimezone: string;
  currentDate: Date;
  qualificationText: string;
  isLoadingQualification: boolean;
  workplaceName: string;
  formattedDistance: string;
  shiftWorkDurationInHours: number;
  hourlyPay: string;
  totalPay: string;
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  isDeclining: boolean;
  isSuccessProcessingInvite: boolean;
  isShortLeadTime: boolean;
  isLoadingRouteEstimate: boolean;
  status: ShiftInviteStatus;
}

export function ShiftInviteCard(props: ShiftInviteCardProps) {
  const {
    timeSlot,
    shiftStart,
    shiftEnd,
    currentDate,
    expiresAt,
    shiftTimezone,
    qualificationText,
    isLoadingQualification,
    workplaceName,
    formattedDistance,
    shiftWorkDurationInHours,
    hourlyPay,
    totalPay,
    onAccept,
    onDecline,
    isAccepting,
    isDeclining,
    isSuccessProcessingInvite,
    isShortLeadTime,
    isLoadingRouteEstimate,
    status,
  } = props;

  const inviteIsExpired = isBefore(expiresAt, currentDate);

  const disabled =
    status === ShiftInviteStatus.DECLINED ||
    status === ShiftInviteStatus.DELETED ||
    inviteIsExpired;

  const deviceTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Card outlined>
      <BookingCardContentWrapper sx={{ gap: 1 }}>
        <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
          <ShiftCardPillsWrapper>
            {isShortLeadTime && !inviteIsExpired ? (
              // shift invite expires at the start date, so `Leave now` is not applicable
              <ShortLeadTimePill shouldLeaveNow={false} />
            ) : (
              <TimeSlotPill timeSlot={timeSlot} />
            )}

            <ShiftInviteStatusPill
              disabled={disabled}
              status={status}
              expiresAt={expiresAt}
              currentDate={currentDate}
            />
          </ShiftCardPillsWrapper>

          <PayInfoLabel totalPay={totalPay} hourlyPay={hourlyPay} />
        </BookingCardSummaryWrapper>

        <ShiftCardSummaryInfo
          dateRange={{
            startDate: shiftStart,
            endDate: shiftEnd,
          }}
          deviceTimezone={deviceTimezone}
          workplaceName={workplaceName}
          durationInHours={shiftWorkDurationInHours}
          formattedDistance={formattedDistance}
          showBreakInfo={false}
          timezone={shiftTimezone}
        />

        <Box sx={{ marginTop: 3 }}>
          <ShiftQualificationLabel
            qualificationText={qualificationText}
            isLoading={isLoadingQualification}
          />
        </Box>

        {status === ShiftInviteStatus.PENDING && !inviteIsExpired && (
          <>
            <ShiftCardDivider />

            <Stack direction="row" justifyContent="space-between" spacing={3}>
              <LoadingButton
                fullWidth
                isLoading={isDeclining}
                disabled={inviteIsExpired || isSuccessProcessingInvite || isAccepting}
                variant="outlined"
                startIconType="close"
                onClick={onDecline}
              >
                Decline
              </LoadingButton>

              <LoadingButton
                fullWidth
                isLoading={isAccepting || isLoadingRouteEstimate}
                disabled={inviteIsExpired || isSuccessProcessingInvite || isDeclining}
                variant="contained"
                startIconType="checkmark"
                onClick={onAccept}
              >
                Accept
              </LoadingButton>
            </Stack>
          </>
        )}
      </BookingCardContentWrapper>
    </Card>
  );
}
